
import { useEffect } from 'react';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

const WhyInvestRuralSouthAfricaPage = () => {
  useEffect(() => {
    document.title = 'Why Invest in Rural South Africa - Comprehensive Market Analysis | TS Dotyeni International';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprehensive analysis of investment opportunities and market potential in rural South African markets. Expert research from TS Dotyeni International on rural development investment strategies and returns.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'rural South Africa investment, market analysis, agricultural investment, rural development, South African economy');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-emerald-900 to-emerald-700 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/f494d9afe6ee6d6c35b9853a4bdd9e95.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-emerald-900/70"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Why Invest in Rural South Africa?
          </h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
            Comprehensive analysis of investment opportunities and market potential in rural South African markets
          </p>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Executive Summary */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Executive Summary</h2>
          <div className="bg-emerald-50 rounded-2xl p-8 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Rural South Africa represents one of the most compelling investment opportunities on the continent, with vast untapped potential in agriculture, renewable energy, and infrastructure development. Government support, abundant natural resources, and growing market demand create an ideal environment for strategic rural investment.
            </p>
          </div>
        </section>

        {/* Market Overview */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Market Overview</h2>
          
          <div className="mb-8">
            <img 
              src="https://readdy.ai/api/search-image?query=South%20African%20rural%20landscape%20with%20modern%20development%20potential%2C%20agricultural%20fields%20with%20irrigation%20systems%2C%20small%20towns%20with%20growth%20opportunities%2C%20economic%20development%20charts%20and%20graphs%20overlay%2C%20professional%20research%20presentation%20style&width=800&height=400&seq=rural-sa-001&orientation=landscape"
              alt="Rural South Africa investment landscape"
              className="w-full h-64 object-cover object-top rounded-xl mb-6"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">R2.8T</div>
              <div className="text-gray-900 font-semibold mb-2">Rural Economy Value</div>
              <div className="text-gray-600">Significant contribution to national GDP</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">18M</div>
              <div className="text-gray-900 font-semibold mb-2">Rural Population</div>
              <div className="text-gray-600">Large consumer and workforce base</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">25%</div>
              <div className="text-gray-900 font-semibold mb-2">Expected Growth</div>
              <div className="text-gray-600">Projected rural economy growth by 2030</div>
            </div>
          </div>
        </section>

        {/* Key Investment Drivers */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Investment Drivers</h2>
          
          <div className="space-y-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                <i className="ri-government-line mr-3 text-emerald-600"></i>
                Government Support & Policy Framework
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                The South African government has implemented comprehensive rural development policies, including the Rural Development and Land Reform Programme, providing tax incentives, grants, and regulatory support for rural investments.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Tax incentives for rural development projects</li>
                <li>Streamlined regulatory processes for rural investments</li>
                <li>Government co-investment opportunities</li>
                <li>Land reform programs creating new opportunities</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                <i className="ri-leaf-line mr-3 text-emerald-600"></i>
                Agricultural Potential
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                South Africa's diverse climate zones and fertile soils support a wide range of agricultural activities. With only 30% of agricultural potential currently utilized, there are massive opportunities for expansion and modernization.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>12% of land suitable for crop production (only 30% utilized)</li>
                <li>Strong export markets for agricultural products</li>
                <li>Growing domestic demand for processed foods</li>
                <li>Opportunities in sustainable farming practices</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                <i className="ri-flashlight-line mr-3 text-emerald-600"></i>
                Energy Infrastructure Needs
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Rural areas face significant energy challenges, creating opportunities for renewable energy projects. Solar and wind resources are abundant, and government policies support independent power producers.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Excellent solar and wind resources</li>
                <li>Government renewable energy procurement programs</li>
                <li>Growing demand for off-grid solutions</li>
                <li>Export potential for green energy</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Investment Opportunities */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Specific Investment Opportunities</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Agro-Processing Facilities</h3>
              <p className="text-gray-600 mb-4">Modern processing facilities for fruits, vegetables, and grains to serve both domestic and export markets.</p>
              <div className="text-emerald-600 font-medium">Expected ROI: 18-25%</div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Rural Retail Networks</h3>
              <p className="text-gray-600 mb-4">Modern retail infrastructure to replace informal trading and improve access to goods and services.</p>
              <div className="text-emerald-600 font-medium">Expected ROI: 15-22%</div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Renewable Energy Projects</h3>
              <p className="text-gray-600 mb-4">Solar and wind farms to serve rural communities and feed into the national grid.</p>
              <div className="text-emerald-600 font-medium">Expected ROI: 20-28%</div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Logistics & Transportation</h3>
              <p className="text-gray-600 mb-4">Warehousing, cold storage, and transportation networks to connect rural producers to markets.</p>
              <div className="text-emerald-600 font-medium">Expected ROI: 16-24%</div>
            </div>
          </div>
        </section>

        {/* Risk Analysis */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Risk Analysis & Mitigation</h2>
          
          <div className="bg-yellow-50 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Risks</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Infrastructure limitations in remote areas</li>
              <li>Skills shortages in rural communities</li>
              <li>Weather and climate variability</li>
              <li>Regulatory and policy changes</li>
            </ul>
          </div>

          <div className="bg-green-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Mitigation Strategies</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Partner with government infrastructure programs</li>
              <li>Invest in local skills development and training</li>
              <li>Implement climate-smart technologies</li>
              <li>Maintain strong government and community relationships</li>
            </ul>
          </div>
        </section>

        {/* Financial Projections */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Financial Projections</h2>
          
          <div className="bg-white border border-gray-200 rounded-xl p-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-emerald-600 mb-2">5-7 Years</div>
                <div className="text-gray-900 font-semibold mb-2">Typical Investment Horizon</div>
                <div className="text-gray-600">Medium to long-term returns</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600 mb-2">15-25%</div>
                <div className="text-gray-900 font-semibold mb-2">Expected IRR Range</div>
                <div className="text-gray-600">Strong risk-adjusted returns</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600 mb-2">2-3x</div>
                <div className="text-gray-900 font-semibold mb-2">Multiple on Investment</div>
                <div className="text-gray-600">Attractive exit multiples</div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Investment Conclusion</h2>
          <div className="bg-emerald-50 rounded-2xl p-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Rural South Africa offers compelling investment opportunities with strong government support, abundant natural resources, and growing market demand. The combination of attractive returns, positive social impact, and strategic government partnerships makes rural South African investment an essential component of any African investment portfolio.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              With proper due diligence, local partnerships, and strategic planning, investors can achieve exceptional returns while contributing to sustainable rural development and economic transformation.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-emerald-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-semibold mb-4">Start Your Rural South Africa Investment Journey</h3>
            <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
              Partner with TS Dotyeni International to access vetted investment opportunities in rural South Africa.
            </p>
            <button className="bg-white text-emerald-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer">
              Request Investment Briefing
            </button>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
};

export default WhyInvestRuralSouthAfricaPage;
